import type { ReactNode } from 'react'
import { Button } from '@/components/ui/Button'
import { Icone } from '@/components/ui/Icone'

/**
 * Les trois portes qui restent ouvertes — [DECISIONS §193].
 *
 * **Nommées ici, une seule fois.** L'écran de fermeture, le bandeau et le
 * panneau de Mon Cercle disaient chacun leur version de « ce qui reste » ;
 * trois listes recopiées finissent par diverger, et c'est le message même qui
 * doit rassurer qui se mettrait à mentir.
 *
 * **La première ligne ne promet PLUS les corrigés** — §191. « Les anciens
 * sujets et leurs corrigés » était vrai jusqu'au jour où la réponse s'est
 * fermée ; le laisser reviendrait à vendre un abonnement en s'appuyant sur une
 * phrase fausse.
 */
const RESTE_OUVERT: { icone: 'document' | 'cible' | 'personnes'; texte: string }[] = [
  {
    icone: 'document',
    texte:
      'Les anciens sujets — l’épreuve entière, ses questions, leurs options et leurs thèmes. '
      + 'La réponse et son explication s’ouvrent avec votre accès.',
  },
  { icone: 'cible', texte: 'Votre classement, entier.' },
  {
    icone: 'personnes',
    texte: 'Mon Cercle — vos amis, et qui vous a envoyé une question.',
  },
]

/**
 * Où mène chacun des trois boutons secondaires.
 *
 * Facultatifs : le panneau intégré de Mon Cercle n'a pas de raison de proposer
 * un lien vers Mon Cercle, et un bouton qui ramène là où l'on est déjà se lit
 * comme une panne.
 */
export interface Sorties {
  // `| undefined` explicitement : `exactOptionalPropertyTypes` distingue
  // « absent » de « présent et indéfini », et un écran qui relaie ses propres
  // sorties facultatives passe forcément la seconde forme.
  onArchives?: (() => void) | undefined
  onClassement?: (() => void) | undefined
  onCercle?: (() => void) | undefined
}

/**
 * Ce que le candidat voit à la place d'un écran fermé — [DECISIONS §157, §167,
 * §193].
 *
 * > « L'écran doit **dire** ce qui se passe — un contenu qui disparaît sans
 * > explication passe pour une panne. »
 *
 * **Ce qui RESTE avant ce qui s'arrête**, et l'ordre est la moitié du message.
 * Le §158 le dit pour le courriel d'expiration — « sans cela le candidat croit
 * avoir tout perdu, y compris ses données, et ne revient pas ».
 *
 * **Et « reste » veut dire OUVERT, pas conservé** — §193. L'écran nommait
 * jusqu'ici les séries, la progression et les marque-pages : trois choses
 * qu'on ne perd pas, mais dont aucune ne dit qu'on peut travailler ce soir. Il
 * nomme désormais les trois écrans qui répondent encore, et chacun porte son
 * bouton.
 *
 * **Trois boutons secondaires ne font pas de cet écran une boutique** — la
 * crainte du §167, qui imposait « un seul bouton ». Elle visait la grille
 * tarifaire et le champ de coupon, qui restent derrière « Reprendre mon
 * accès » ; trois liens vers des écrans GRATUITS disent l'inverse d'une
 * boutique.
 */
export function AccesTermine({
  ecran,
  onAbonnement,
  onArchives,
  onClassement,
  onCercle,
}: {
  /** Ce qui est fermé, dit en toutes lettres — jamais « ce contenu ». */
  ecran: string
  onAbonnement: () => void
} & Sorties) {
  return (
    <section className="flex flex-col gap-4 px-4 py-10" role="status">
      <div className="flex items-center gap-2">
        <Icone nom="cadenas" taille={20} />
        <h2 className="text-[20px] font-extrabold">Votre accès est terminé</h2>
      </div>

      <p className="text-[15px]">
        Rien n’a été supprimé. Vos séries, votre progression et vos marque-pages vous
        attendent exactement où vous les avez laissés.
      </p>

      <ListeDeCeQuiReste />

      {ecran !== '' && (
        <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
          En pause jusqu’à votre prochain accès&nbsp;: {ecran}.
        </p>
      )}

      <Boutons
        onAbonnement={onAbonnement}
        onArchives={onArchives}
        onClassement={onClassement}
        onCercle={onCercle}
      />
    </section>
  )
}

/**
 * Le bandeau de la coque — il suit le candidat sur les écrans RESTÉS ouverts.
 *
 * **Sans lui, les archives seraient le seul écran silencieux.** Elles
 * fonctionnent, mais amputées : leurs thèmes ne mènent plus nulle part (§157)
 * et la réponse ne s'ouvre plus (§191). Un candidat qui tape un thème et ne
 * voit rien se produire conclut à une panne, alors que c'est la règle qui
 * s'applique. Le bandeau donne à cet écran la phrase qui lui manque.
 *
 * Il ne s'affiche PAS par-dessus l'écran de fermeture lui-même : deux fois le
 * même message sur la même page se lit comme un défaut d'affichage.
 */
export function BandeauAccesTermine({ onAbonnement }: { onAbonnement: () => void }) {
  return (
    <section
      role="status"
      className="flex flex-col gap-2 border-b p-4"
      style={{ borderColor: 'var(--bordure)', background: 'var(--surface-2)' }}
    >
      <h2 className="text-[15px] font-bold">Votre accès est terminé</h2>

      <p className="text-[13px]" style={{ color: 'var(--texte-2)' }}>
        {/* §191 — le bandeau disait « leurs corrigés restent ». Il ne le dit
            plus : la réponse s’est fermée en même temps que le reste, et
            l’annoncer ouverte se découvrirait au premier sujet consulté. */}
        Les anciens sujets restent consultables — leurs thèmes n’ouvrent plus de leçon
        ni de quiz, et la réponse s’ouvre avec votre accès. Votre classement et votre
        cercle sont intacts.
      </p>

      <Button size="dense" variant="outline" className="self-start" onClick={onAbonnement}>
        Mon abonnement
      </Button>
    </section>
  )
}

/**
 * Le même message, en panneau posé DANS un écran resté ouvert — §193.
 *
 * Mon Cercle en a besoin : ses amis répondent, ses partages non. Rendre le
 * grand écran de fermeture à la place de l'onglet entier effacerait la liste
 * d'amis, qui doit rester ; ne rien dire ferait passer une boîte fermée pour
 * une boîte vide.
 *
 * `enTete` porte ce que l'écran hôte a de plus précis à dire — les annonces,
 * dans le cas de Mon Cercle. Le panneau, lui, ne sait dire que la règle.
 */
export function PanneauAccesTermine({
  titre,
  explication,
  enTete,
  onAbonnement,
  onArchives,
  onClassement,
}: {
  titre: string
  explication: string
  enTete?: ReactNode
  onAbonnement: () => void
} & Sorties) {
  return (
    <section
      role="status"
      className="m-4 flex flex-col gap-3 rounded-2xl border p-4"
      style={{ borderColor: 'var(--bordure)', background: 'var(--surface-2)' }}
    >
      {enTete}

      <div className="flex items-center gap-2">
        <Icone nom="cadenas" taille={18} />
        <h3 className="text-[17px] font-bold">{titre}</h3>
      </div>

      <p className="text-[15px]" style={{ color: 'var(--texte-2)' }}>
        {explication}
      </p>

      <Boutons
        onAbonnement={onAbonnement}
        onArchives={onArchives}
        onClassement={onClassement}
      />
    </section>
  )
}

function ListeDeCeQuiReste() {
  return (
    <div className="flex flex-col gap-2">
      <h3 className="text-[15px] font-bold">Ce qui reste ouvert</h3>

      <ul className="flex flex-col gap-2">
        {RESTE_OUVERT.map((porte) => (
          <li key={porte.icone} className="flex items-start gap-2 text-[15px]">
            <span className="pt-[3px]">
              <Icone nom={porte.icone} taille={16} />
            </span>
            <span>{porte.texte}</span>
          </li>
        ))}
      </ul>
    </div>
  )
}

/**
 * « Reprendre mon accès » d'abord, les portes gratuites ensuite.
 *
 * L'ordre est le même qu'au §191 : le lien gratuit est en SECOND rôle. Le
 * mettre devant ferait de la reprise d'abonnement une option parmi d'autres,
 * sur l'écran qui existe précisément pour la proposer.
 */
function Boutons({
  onAbonnement,
  onArchives,
  onClassement,
  onCercle,
}: { onAbonnement: () => void } & Sorties) {
  return (
    <div className="flex flex-wrap gap-2">
      <Button onClick={onAbonnement}>Reprendre mon accès</Button>

      {/* **« J'ai un code » se dit ICI** — [DECISIONS §243]. Ben : « je ne vois
          pas encore les possibilités d'utiliser les coupons pour l'abonnement ».
          Le champ existait, la route existait, l'échange marchait — mais le mot
          « code » n'apparaissait NULLE PART avant d'avoir atteint l'écran
          d'abonnement, deux tapes plus loin.

          §157 avait rendu le champ discret pour une raison juste : « un champ de
          code mis en avant fait chercher un code à qui n'en a pas ; il quitte
          l'écran pour en trouver un, et ne revient pas payer. » La discrétion a
          dépassé son but — celui à qui l'on a DONNÉ un code n'avait aucun moyen
          de savoir que l'application en accepte.

          Le bouton mène au même écran que « Reprendre mon accès », où le champ
          attend. Il est secondaire, comme les trois autres : ce qui change,
          c'est que le mot existe. */}
      <Button size="dense" variant="outline" onClick={onAbonnement}>
        J’ai un code
      </Button>

      {onArchives !== undefined && (
        <Button size="dense" variant="outline" onClick={onArchives}>
          Voir les archives
        </Button>
      )}

      {onClassement !== undefined && (
        <Button size="dense" variant="outline" onClick={onClassement}>
          Mon classement
        </Button>
      )}

      {onCercle !== undefined && (
        <Button size="dense" variant="outline" onClick={onCercle}>
          Mon Cercle
        </Button>
      )}
    </div>
  )
}
